import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { Building2, Calendar, ExternalLink, Loader2, Search } from "lucide-react";
import { useState } from "react";
import { useTRPC } from "@/integrations/trpc/react";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { getStatusColor, getStatusLabel, getEffectiveStatus } from "@/lib/utils";

// Local solicitation type
interface Solicitation {
	id: string;
	title: string;
	description: string;
	type: string;
	status: string;
	dueDate: string | null;
	publishDate: string | null;
	memberOrg?: string;
}

export const Route = createFileRoute("/embed/$buyerId")({
	component: EmbeddableSolicitationsPage,
});

function EmbeddableSolicitationsPage() {
	const { buyerId } = Route.useParams();
	const trpc = useTRPC();
	const [search, setSearch] = useState("");

	// Query public solicitations
	const { data: solicitationsData, isLoading } = useQuery(
		trpc.public.solicitations.list.queryOptions({
			buyerOrgId: buyerId,
			limit: 100,
			offset: 0,
		}),
	);

	// Query organization info
	const { data: orgData } = useQuery(
		trpc.public.organizations.get.queryOptions({
			organizationId: buyerId,
		}),
	);

	// Map to local type
	const publicSolicitations: Solicitation[] = (solicitationsData ?? []).map((s) => ({
		id: s.id,
		title: s.title,
		description: s.description ?? "",
		type: s.type,
		status: getEffectiveStatus(s.status, s.publish_date, s.due_date),
		dueDate: s.due_date,
		publishDate: s.publish_date,
		memberOrg: undefined, // Could be fetched from member_org_id
	}));

	const filteredSolicitations = publicSolicitations.filter((sol) => {
		if (search) {
			const searchLower = search.toLowerCase();
			return (
				sol.title.toLowerCase().includes(searchLower) ||
				sol.type.toLowerCase().includes(searchLower) ||
				sol.description.toLowerCase().includes(searchLower)
			);
		}
		return true;
	});

	return (
		<div className="min-h-screen bg-white">
			{/* Header */}
			<div className="border-b bg-gray-50 px-4 py-4">
				<div className="flex items-center justify-between">
					<div>
						<h1 className="text-lg font-semibold text-gray-900">
							{orgData?.name ?? "Open Solicitations"}
						</h1>
						<p className="text-sm text-gray-500">
							{isLoading ? "Loading..." : `${filteredSolicitations.length} active opportunities`}
						</p>
					</div>
					<a
						href={`/public?buyerId=${buyerId}`}
						target="_blank"
						rel="noopener noreferrer"
						className="text-sm text-blue-600 hover:underline flex items-center gap-1"
					>
						View full portal
						<ExternalLink className="h-3 w-3" />
					</a>
				</div>
			</div>

			{/* Search */}
			<div className="px-4 py-3 border-b">
				<div className="relative">
					<Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
					<Input
						placeholder="Search solicitations..."
						value={search}
						onChange={(e) => setSearch(e.target.value)}
						className="pl-10"
					/>
				</div>
			</div>

			{/* Solicitation List */}
			<div className="divide-y">
				{isLoading ? (
					<div className="flex items-center justify-center py-12">
						<Loader2 className="h-8 w-8 animate-spin text-gray-400" />
					</div>
				) : filteredSolicitations.map((solicitation) => (
					<a
						key={solicitation.id}
						href={`/public/solicitations/${solicitation.id}`}
						target="_blank"
						rel="noopener noreferrer"
						className="block px-4 py-4 hover:bg-gray-50 transition-colors"
					>
						<div className="flex items-start justify-between gap-4">
							<div className="flex-1 min-w-0">
								<div className="flex items-center gap-2 mb-1">
									<Badge variant="outline" className="text-xs">
										{solicitation.type}
									</Badge>
									<Badge className={getStatusColor(solicitation.status)}>
										{getStatusLabel(solicitation.status)}
									</Badge>
								</div>
								<h3 className="font-medium text-gray-900 mb-1 line-clamp-1">
									{solicitation.title}
								</h3>
								<p className="text-sm text-gray-500 line-clamp-2 mb-2">
									{solicitation.description}
								</p>
								<div className="flex items-center gap-4 text-xs text-gray-400">
									{solicitation.dueDate && (
										<span className="flex items-center gap-1">
											<Calendar className="h-3 w-3" />
											Due: {new Date(solicitation.dueDate).toLocaleDateString()}
										</span>
									)}
									{solicitation.memberOrg && (
										<span className="flex items-center gap-1">
											<Building2 className="h-3 w-3" />
											{solicitation.memberOrg}
										</span>
									)}
								</div>
							</div>
							<ExternalLink className="h-4 w-4 text-gray-400 flex-shrink-0 mt-1" />
						</div>
					</a>
				))}

				{!isLoading && filteredSolicitations.length === 0 && (
					<div className="px-4 py-12 text-center text-gray-500">
						<Building2 className="h-12 w-12 mx-auto mb-3 opacity-50" />
						<p>No solicitations found</p>
						{search && (
							<p className="text-sm mt-1">Try adjusting your search criteria</p>
						)}
					</div>
				)}
			</div>

			{/* Footer */}
			<div className="border-t px-4 py-3 text-center text-xs text-gray-400">
				Powered by{" "}
				<a
					href="https://bidscope.gov"
					target="_blank"
					rel="noopener noreferrer"
					className="text-blue-600 hover:underline"
				>
					Bidscope
				</a>
			</div>
		</div>
	);
}
